import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import '../../App.css';
import { api } from '../../api/client';
import SchemaForm from '../../schemaForm/SchemaForm';
import AddressField, { AddressValue } from '../../schemaForm/fields/AddressField';
import { missingAddressParts } from '../../utils/validation';
import EmergencyContactField, { EmergencyContactValue } from '../../schemaForm/fields/EmergencyContactField';
import UserSelectField from './UserSelectField';
import MemberGroupsField from '../../schemaForm/fields/MemberGroupsField';
import type { SelectId } from '../MultiSelect';
import type { SchemaFormSection } from '../../schemaForm/types';
import type { TargetOption } from '../../schemaForm/targets';
import type { SeedKey } from '../../hooks/useSeedEffect';
import { useValuesDirty } from '../../hooks/useValuesDirty';

/**
 * Payload submitted by MemberForm. Built from SchemaForm values. Address is now
 * the backend's jsonb object (the route prefers `address` over the legacy flat
 * city/street/... keys, so those are no longer sent). `name` is intentionally
 * omitted — the backend computes it from firstName/lastName.
 */
export interface IMemberFormData {
  firstName?: string;
  lastName?: string;
  description?: string;
  user?: string;
  /** Never sent: plain-group changes go through the D6 prompt (absent = unchanged). */
  groups?: SelectId[];
  activityIds: SelectId[];
  dateOfBirth?: string;
  nationality?: string;
  birthNumber?: string;
  email?: string;
  phone?: string;
  color?: string;
  address?: AddressValue | null;
  emergencyContact?: EmergencyContactValue | null;
  allergies?: string;
  medicalNotes?: string;
}

/** Plain-group diff against the seed, for the host to run through the D6 prompt. */
export interface MemberGroupChanges {
  add: number[];
  remove: number[];
}

interface IMemberFormProps {
  initialData?: {
    name: string;
    firstName?: string;
    lastName?: string;
    description: string;
    user?: string;
    groups: string[];
    activityIds?: (string | number)[];
    dateOfBirth?: string;
    city?: string;
    cityDistrict?: string;
    nationality?: string;
    birthNumber?: string;
    street?: string;
    zipCode?: string;
    buildingNumber?: string;
    streetNumber?: string;
    email?: string;
    phone?: string;
    color?: string;
    allergies?: string;
    medicalNotes?: string;
    /** Backend jsonb address; falls back to the flat fields above. */
    address?: AddressValue | null;
    emergencyContact?: EmergencyContactValue | null;
  };
  /** Record identity — seeds once per record instead of per object identity. */
  seedKey?: SeedKey;
  /** Reports whether the form still matches its seed (host modals guard on it). */
  onDirtyChange?: (dirty: boolean) => void;
  onSubmit: (data: IMemberFormData, groupChanges: MemberGroupChanges) => Promise<void>;
  loading: boolean;
  submitButtonText: string;
  isUserView?: boolean;
  /**
   * Scoped admin's allowed activity ids. Retained for API compatibility; the
   * backend still rejects out-of-scope activityIds on submit, so dropping the
   * pre-filter is safe. (See report note.)
   */
  allowedActivityIds?: number[];
}

/**
 * Thin wrapper around the schema-driven SchemaForm. Preserves the external
 * props the three consuming pages (MemberCreatePage, MemberEditPage,
 * MyMembersPage) rely on, while delegating field rendering to entity meta.
 *
 * Note: the former `requiredFields` org-settings handling is now covered by the
 * backend meta (GET /api/meta/member merges `requiredMemberFields`), so
 * SchemaForm's required handling supersedes the old client-side requiredFields.
 */
const MemberForm: React.FC<IMemberFormProps> = ({
  initialData,
  seedKey,
  onDirtyChange,
  onSubmit,
  loading,
  submitButtonText,
  isUserView = false,
  allowedActivityIds,
}) => {
  const { t } = useTranslation();

  // Seed SchemaForm values from initialData, mapping the legacy flat address
  // fields into the address object when the jsonb object isn't present.
  const initialValues = useMemo(() => {
    if (!initialData) return undefined;
    const address: AddressValue =
      initialData.address ?? {
        city: initialData.city,
        cityDistrict: initialData.cityDistrict,
        street: initialData.street,
        zipCode: initialData.zipCode,
        buildingNumber: initialData.buildingNumber,
        streetNumber: initialData.streetNumber,
      };
    return {
      firstName: initialData.firstName ?? '',
      lastName: initialData.lastName ?? '',
      description: initialData.description ?? '',
      user: initialData.user ?? '',
      groups: (initialData.groups || []).map(Number).filter(Number.isFinite),
      activityIds: (initialData.activityIds || []).map(Number).filter(Number.isFinite),
      dateOfBirth: initialData.dateOfBirth ? initialData.dateOfBirth.split('T')[0] : '',
      nationality: initialData.nationality ?? '',
      birthNumber: initialData.birthNumber ?? '',
      email: initialData.email ?? '',
      phone: initialData.phone ?? '',
      color: initialData.color ?? '',
      address,
      emergencyContact: initialData.emergencyContact ?? null,
      allergies: initialData.allergies ?? '',
      medicalNotes: initialData.medicalNotes ?? '',
    };
  }, [initialData]);

  const handleValuesChange = useValuesDirty(initialValues, onDirtyChange);

  const handleSubmit = (values: Record<string, any>) => {
    // Optional scalars used to go out as `values.x || undefined`, which drops
    // the key from the JSON body. The backend reads an absent key as
    // "unchanged" (buildWriteValues skips keys the body does not own), so
    // CLEARING a field saved nothing — the page reported success, navigated
    // away, and the old value came back on reload.
    //
    // Sending '' unconditionally would fix the clear and open a worse hole: a
    // surface whose GET does not return a field (PII gating, a narrower
    // serializer) seeds it blank, and a save would then wipe stored data the
    // form never showed. So distinguish the two cases against the seed —
    // blank-and-was-blank is omitted, blank-and-was-set is an explicit clear
    // (coerceByKind maps '' to null).
    const seed = (initialValues ?? {}) as Record<string, any>;
    const clearable = (key: string) => {
      const next = values[key];
      const wasSet = seed[key] !== undefined && seed[key] !== '' && seed[key] !== null;
      if (next === undefined || next === null || next === '') {
        return wasSet ? '' : undefined;
      }
      return next;
    };

    const payload: IMemberFormData = {
      firstName: values.firstName || undefined,
      lastName: values.lastName || undefined,
      description: values.description ?? '',
      user: values.user || undefined,
      activityIds: values.activityIds ?? [],
      dateOfBirth: clearable('dateOfBirth'),
      nationality: clearable('nationality'),
      birthNumber: clearable('birthNumber'),
      email: clearable('email'),
      phone: clearable('phone'),
      color: clearable('color'),
      address: values.address ?? null,
      emergencyContact: values.emergencyContact ?? null,
      allergies: clearable('allergies'),
      medicalNotes: clearable('medicalNotes'),
    };
    // The groups field only edits plain groups; managed ids are echoed unchanged.
    const toIds = (v: unknown) =>
      (Array.isArray(v) ? v : []).map(Number).filter(Number.isFinite) as number[];
    const before = new Set(toIds(seed.groups));
    const after = new Set(toIds(values.groups));
    const groupChanges: MemberGroupChanges = isUserView
      ? { add: [], remove: [] }
      : {
          add: [...after].filter((id) => !before.has(id)),
          remove: [...before].filter((id) => !after.has(id)),
        };
    return onSubmit(payload, groupChanges);
  };

  const sections: SchemaFormSection[] = useMemo(() => {
    // The admin-only "assign to user" select and the activity assignment are
    // only present on the admin variant. The user dashboard variant forces the
    // current tenant user server-side and assigns activities elsewhere.
    const otherItems: Array<string> = ['description', 'color'];
    if (!isUserView) otherItems.push('user');
    // Member-side group requesting is retired; groups stay admin-only.
    if (!isUserView) otherItems.push('groups');
    if (!isUserView) otherItems.push('activityIds');

    return [
      {
        titleKey: 'members.sectionBasic',
        columns: 2,
        items: ['firstName', 'lastName', 'dateOfBirth', 'nationality', 'birthNumber'],
      },
      {
        titleKey: 'members.sectionContact',
        columns: 1,
        items: ['address', 'email', 'phone'],
      },
      {
        titleKey: 'members.sectionHealth',
        columns: 1,
        items: ['allergies', 'medicalNotes', 'emergencyContact'],
      },
      {
        titleKey: 'members.sectionOther',
        columns: 1,
        items: otherItems,
      },
    ];
  }, [isUserView]);

  // Relation option fetchers: groups are admin-only (member-side group
  // requesting is retired). For scoped admins, the activity list is pre-filtered
  // to the allowed ids.
  const relationOptions = useMemo(() => {
    const map: Record<string, () => Promise<TargetOption[]>> = {};
    if (allowedActivityIds && allowedActivityIds.length > 0) {
      const allowed = new Set(allowedActivityIds.map(Number));
      map.activityIds = () =>
        api.activities.list().then((r) =>
          (r.data as any[])
            .map((a) => ({ _id: a._id ?? a.id, name: a.name }))
            .filter((o) => allowed.has(Number(o._id)))
        );
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }, [allowedActivityIds]);

  // The generic blank check treats the address object as non-empty even when
  // every box in it is blank, so a required address needs its own validator.
  // Only enforced when the org actually requires the address (meta.required).
  const validate = useMemo(
    () => ({
      address: (value: any, _values: Record<string, any>, field?: { required: boolean }) => {
        if (!field?.required) return null;
        const missing = missingAddressParts(value as AddressValue | null);
        if (missing.length === 0) return null;
        return t('members.addressIncomplete', {
          fields: missing.map((part) => t(`members.${part}`)).join(', '),
        });
      },
    }),
    [t]
  );

  return (
    <SchemaForm
      entity="member"
      sections={sections}
      overrides={{
        address: AddressField,
        emergencyContact: EmergencyContactField,
        user: UserSelectField,
        groups: MemberGroupsField,
      }}
      validate={validate}
      relationOptions={relationOptions}
      initialValues={initialValues}
      seedKey={seedKey}
      onValuesChange={handleValuesChange}
      onSubmit={handleSubmit}
      submitLabel={submitButtonText}
      loading={loading}
    />
  );
};

export default MemberForm;
